/* Drag-to-scroll for horizontal tracks.

   Add `data-drag-scroll` to any element that scrolls horizontally (`overflow-x: auto`).

   Touch already scrolls natively, so this only adds MOUSE dragging — pointer events are not used
   because they would hijack the native touch scrolling that already works.

   Mirrors the behaviour already implemented inline in ctv-watch-band / ctv-catchup-main: an
   `is-dragging` class while held, and a capture-phase click suppressor so ending a drag over a card
   does not follow its link. Those two sections keep their own copies — adding the attribute there
   as well would bind two handlers to the same track.

   Smoothness notes:
   - scrollLeft is written once per ANIMATION FRAME, not once per mousemove. A mouse can fire well
     above 60Hz, and writing scroll position on every event forces layout each time, which is what
     made the drag feel jerky.
   - Releasing the button hands over to a momentum glide with friction, so the track eases to a stop
     instead of dead-stopping and then being yanked by scroll-snap.
   - scroll-snap and scroll-behavior are suppressed INLINE for the duration of the drag and glide,
     so every consuming section gets the fix without needing its own CSS.

   Used by: cc-product-carousel grids, ctv-schedule day bar, inspiration-filters group row,
   inspiration-listing, article-grid. */
(() => {
  'use strict';

  var DRAG_THRESHOLD = 6; // px of movement before a click is treated as a drag, not a tap
  var FRICTION = 0.9; // per frame; lower = stops sooner. Glide distance ~ velocity * 16 / (1 - FRICTION).
  var MIN_VELOCITY = 0.02; // px/ms below which the glide ends
  var MAX_VELOCITY = 2.5; // px/ms cap. With the friction above this caps a flick at ~400px, roughly one card.

  function initTrack(track) {
    if (!track || track.dataset.dragScrollReady === '1') return;
    track.dataset.dragScrollReady = '1';

    var isDown = false;
    var startX = 0;
    var startScroll = 0;
    var moved = 0;

    var targetScroll = 0; // where the drag wants the track to be; applied on the next frame
    var frame = null; // rAF id for the drag writer
    var glideFrame = null; // rAF id for the momentum glide
    var velocity = 0; // px/ms, sign follows scroll direction
    var lastX = 0;
    var lastTime = 0;

    // Snap and smooth-scroll both fight a drag: snap pulls the track to card boundaries mid-gesture,
    // and `scroll-behavior: smooth` animates every write. Suppressed inline for the gesture only.
    var prevSnap = '';
    var prevBehavior = '';

    function suppressSnap() {
      prevSnap = track.style.scrollSnapType;
      prevBehavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
    }

    function restoreSnap() {
      track.style.scrollSnapType = prevSnap;
      track.style.scrollBehavior = prevBehavior;
      settleToNearest();
    }

    /* Opt-in via `data-drag-snap`: after a drag the browser does NOT re-apply scroll-snap to the
       position you let go at — CSS snapping only acts on ITS own scrolls — so the track can rest
       showing half a card. This eases to the nearest child boundary instead.
       Deliberately opt-in: tracks like the blog scroller are meant to show a partial card as an
       affordance that there is more to the right. */
    function settleToNearest() {
      if (!track.hasAttribute('data-drag-snap')) return;
      var kids = track.children;
      if (!kids.length) return;

      var trackLeft = track.getBoundingClientRect().left;
      var padLeft = parseFloat(getComputedStyle(track).paddingLeft) || 0;
      var best = null;
      var bestDist = Infinity;
      for (var i = 0; i < kids.length; i++) {
        if (kids[i].classList && kids[i].classList.contains('is-clone')) continue;
        var delta = kids[i].getBoundingClientRect().left - trackLeft - padLeft;
        if (Math.abs(delta) < bestDist) {
          bestDist = Math.abs(delta);
          best = delta;
        }
      }
      if (best === null || Math.abs(best) < 1) return;

      var target = track.scrollLeft + best;
      var max = track.scrollWidth - track.clientWidth;
      track.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
    }

    function writeFrame() {
      frame = null;
      if (!isDown) return;
      track.scrollLeft = targetScroll;
    }

    function stopGlide() {
      if (glideFrame) {
        cancelAnimationFrame(glideFrame);
        glideFrame = null;
      }
    }

    function glide() {
      velocity *= FRICTION;
      // `velocity` is already in SCROLL direction (see the mousemove sampler, which negates pointer
      // movement), so it is added here. Subtracting sent the glide backwards past the drag origin.
      var next = track.scrollLeft + velocity * 16; // 16ms ≈ one frame
      var max = track.scrollWidth - track.clientWidth;
      var clamped = Math.max(0, Math.min(max, next));
      track.scrollLeft = clamped;

      // Stop at the ends rather than grinding against them.
      if (Math.abs(velocity) < MIN_VELOCITY || clamped !== next) {
        glideFrame = null;
        restoreSnap();
        return;
      }
      glideFrame = requestAnimationFrame(glide);
    }

    track.addEventListener('mousedown', function (e) {
      // Ignore anything but the primary button, and let form controls behave normally.
      if (e.button !== 0) return;
      stopGlide();
      suppressSnap();
      isDown = true;
      moved = 0;
      velocity = 0;
      startX = e.pageX - track.offsetLeft;
      startScroll = track.scrollLeft;
      targetScroll = startScroll;
      lastX = startX;
      lastTime = e.timeStamp || performance.now();
      // NOTE: 'is-dragging' is NOT added here. It used to be, but that made the CSS rule below
      // (`.is-dragging a, .is-dragging img { pointer-events: none; }`) apply on every mousedown,
      // including a plain click with zero movement — the browser then re-hit-tests the very next
      // mouseup/click with the link/image already excluded, so the default navigation silently
      // never fired even though hovering (before mousedown) showed the correct href. It's added
      // instead in the mousemove handler, only once real movement crosses DRAG_THRESHOLD, so a tap
      // is never mistaken for the start of a drag.
    });

    function release() {
      if (!isDown) return;
      isDown = false;
      track.classList.remove('is-dragging');
      if (frame) {
        cancelAnimationFrame(frame);
        frame = null;
      }
      if (Math.abs(velocity) > MIN_VELOCITY) {
        glideFrame = requestAnimationFrame(glide);
      } else {
        restoreSnap();
      }
    }

    ['mouseleave', 'mouseup'].forEach(function (ev) {
      track.addEventListener(ev, release);
    });

    // Kill the browser's native image/link drag. Without this, pressing on a card image starts an
    // HTML5 drag: the ghost image follows the cursor, mousemove stops firing at the track, and the
    // slider then jumps to catch up when the button is released.
    track.addEventListener('dragstart', function (e) {
      e.preventDefault();
    });

    track.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      e.preventDefault(); // stops the browser's native text/image drag

      var x = e.pageX - track.offsetLeft;
      moved = Math.abs(x - startX);
      targetScroll = startScroll - (x - startX);

      // Only now — once real movement has crossed the threshold — is this treated as an actual
      // drag: the class drives the CSS that disables pointer-events on links/images for the rest
      // of the gesture, so a real drag ending over a different card can't accidentally follow it.
      if (moved > DRAG_THRESHOLD && !track.classList.contains('is-dragging')) {
        track.classList.add('is-dragging');
      }

      // Velocity from the last sample, for the release glide. Guard dt so a burst of events in the
      // same millisecond cannot divide by zero and produce Infinity.
      var now = e.timeStamp || performance.now();
      var dt = now - lastTime;
      if (dt > 0) {
        var v = -(x - lastX) / dt;
        velocity = Math.max(-MAX_VELOCITY, Math.min(MAX_VELOCITY, v));
        lastX = x;
        lastTime = now;
      }

      if (!frame) frame = requestAnimationFrame(writeFrame);
    });

    // Capture phase so it runs before the link's own handler.
    track.addEventListener(
      'click',
      function (e) {
        if (moved > DRAG_THRESHOLD) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  function initAll() {
    document.querySelectorAll('[data-drag-scroll]').forEach(initTrack);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders sections without a page load. Also used by sections that inject markup
  // client-side (the PDP cross-sell) to bind newly added tracks.
  // NOTE: call with no argument — passing the Event through meant `initAll(event)`, and an Event has
  // no querySelectorAll, so re-init threw instead of binding anything.
  document.addEventListener('shopify:section:load', function () {
    initAll();
  });
})();
